import React, { useState, useEffect,useRef  } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CategorySection from "../components/Category";
import ProductCard from "../components/product/ProductCard";
import { useCart } from "../context/cartContext";
import { useWishlist } from "../context/wishlistContext";
import FilterSidebar from "../components/SidebarFilter";
import Divider from "../components/Divider";

const CategoryPage = () => {
  const { category_id } = useParams();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(category_id);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [sortOption, setSortOption] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const { addToWishlist } = useWishlist();
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllCategories();
  }, []);

  useEffect(() => {
    if (category_id) {
      setSelectedCategory(category_id);
      fetchCategoryData(category_id);
    }
  }, [category_id]);

  useEffect(() => {
    handleApplyFilters();
  }, [selectedSubcategory, products, sortOption]);

  const fetchCategoryData = async (category_id) => {
    setLoading(true);
    try {
      const [productsResponse, subcategoriesResponse] = await Promise.all([
        axios.get(`https://api.theonlysale.com/api/product/category/${category_id}`),
        axios.get(
          `https://api.theonlysale.com/api/subcategories/getByCategory/${category_id}`
        ),
      ]);
  
      const productsWithUserStatus = await Promise.all(
        productsResponse.data.map(async (product) => {
          let imagesArray = [];
          try {
            imagesArray = JSON.parse(product.images);
          } catch (e) {
            console.error("Error parsing images array:", e);
          }
  
          const firstImage = imagesArray.length > 0 ? imagesArray[0] : null;
  
          // Fetch user status for each product's user_id
          let userStatus = null;
          try {
            const userResponse = await axios.get(
              `https://api.theonlysale.com/api/user/get/status/${product.user_id}`
            );
            userStatus = userResponse.data.status;
          } catch (error) {
            console.error(`Error fetching user status for user ${product.user_id}:`, error);
          }
  
          return {
            ...product,
            imageURI: firstImage
              ? `https://api.theonlysale.com/uploads/images/${firstImage.split(/[\\/]/).pop()}`
              : null,
            userStatus, // Add the user's status to the product
          };
        })
      );
  
      // Filter for active user products and sort by stock availability
      const activeProducts = productsWithUserStatus
        .filter((product) => product.userStatus === "active")
        .sort((a, b) => (b.stock > 0) - (a.stock > 0)); // In-stock products first
  
      setProducts(activeProducts);
      setFilteredProducts(activeProducts);
      setSubcategories(subcategoriesResponse.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching category data:", error);
      setLoading(false);
    }
  };
  

  const fetchAllCategories = async () => {
    try {
      const response = await axios.get(
        `https://api.theonlysale.com/api/categories/get`
      );
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleApplyFilters = (filters = {}) => {
    const { rating, minPrice, maxPrice, sortOption } = filters;
  
    const min = Number(minPrice) || 0;
    const max = Number(maxPrice) || Infinity;
  
    let filtered = products.filter((product) => {
      const productPrice = Number(product.final_price);
      const meetsPrice = productPrice >= min && productPrice <= max;
  
      const productRating = product.average_rating
        ? Math.floor(Number(product.average_rating))
        : null;
      const meetsRating = rating
        ? productRating !== null && productRating === Number(rating)
        : true;
  
      const meetsSubcategory = selectedSubcategory
        ? product.subcategory_id === selectedSubcategory
        : true;
  
      return meetsPrice && meetsRating && meetsSubcategory;
    });
  
    // Sort by stock availability first: In-stock products first
    filtered = filtered.sort((a, b) => {
      const stockA = a.stock > 0 ? 1 : 0; // In stock
      const stockB = b.stock > 0 ? 1 : 0; // In stock
      return stockB - stockA; // Sort so that products with stock appear first
    });
  
    // Sort by user-selected option (e.g., name, price, discount)
    if (sortOption) {
      filtered = filtered.sort((a, b) => {
        switch (sortOption) {
          case "name-asc":
            return a.name.localeCompare(b.name);
          case "name-desc":
            return b.name.localeCompare(a.name);
          case "price-asc":
            return a.final_price - b.final_price;
          case "price-desc":
            return b.final_price - a.final_price;
          case "discount":
            return b.discount - a.discount;
          default:
            return 0;
        }
      });
    }
  
    setFilteredProducts(filtered);
  };
  
  const getFilterDescription = (selectedSubcategory, sortOption) => {
    if (selectedSubcategory && sortOption) {
      return `Filtering by ${selectedSubcategory} and sorting by ${sortOption.replace("-", " ")}`;
    } else if (selectedSubcategory) {
      return `Filtering by ${selectedSubcategory}`;
    } else if (sortOption) {
      return `Sorting by ${sortOption.replace("-", " ")}`;
    } else {
      return "No filters applied";
    }
  };

  const handleSortOptionChange = (option) => {
    setSortOption(option);
  };

  const handleClearFilters = () => {
    setSortOption(null);
    setSelectedSubcategory(null);
    setFilteredProducts(products);
  };

  const handleCategoryPress = (category_id) => {
    setSelectedCategory(category_id);
    setSelectedSubcategory(null);
    navigate(`/categories/${category_id}`);
  };

  const handleSubcategoryPress = (subcategory_id) => {
    setSelectedSubcategory(subcategory_id);
  };
  const handleAddToWishlist = (product) => {
    addToWishlist(product);
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    console.log("Product added");
  };

  const selectedCategoryName =
    categories.find((cat) => cat.category_id === parseInt(selectedCategory))
      ?.name || "Category";

  const selectedCategoryDescription =
    categories.find((cat) => cat.category_id === parseInt(selectedCategory))
      ?.description || "Category";
      const scrollRef = useRef(null); // Create a reference for the subcategories div

      const handleScroll = (direction) => {
        const scrollAmount = 200; // Adjust based on how much you want to scroll
        if (scrollRef.current) {
          scrollRef.current.scrollBy({
            left: direction === "left" ? -scrollAmount : scrollAmount,
            behavior: "smooth", // Smooth scroll effect
          });
        }
      };
  return (
    <>
      <Header />

      <div className=" mx-auto p-4 bg-white dark:bg-gray-900">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Shop Our Curated Collection of Vintage Treasures
          </h1>
          <p className="mt-2 text-lg text-gray-700 dark:text-gray-300">
            Dive into our carefully selected range of vintage and pre-loved
            items. Each piece is unique and full of character, offering you
            exceptional quality and timeless style. Find your next favorite
            treasure today!
          </p>
        </div>

        <CategorySection
          categories={categories}
          selectedCategory={selectedCategory}
          handleCategoryPress={handleCategoryPress}
        />
        <h2 className="text-3xl font-extrabold mb-4 text-gray-900 dark:text-gray-100">
          {selectedCategoryName}
        </h2>
        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">
          {selectedCategoryDescription}
        </h3>
        <div className="mb-6">
          <FilterSidebar
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
          />
        </div>

        <p className="mb-6 text-lg text-gray-800 dark:text-gray-200">
          {getFilterDescription()}
        </p>

        {selectedCategory && subcategories.length > 0 && (
  <div className="w-full mb-6">
   

    {/* Arrows for navigation */}
    <div className="flex justify-between items-center">
      <button
        onClick={() => handleScroll("left")}
        className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
        aria-label="Scroll left"
      >
        {/* Left arrow (could use an icon or emoji) */}
        &#9664;
      </button>

      <div
        ref={scrollRef} // Attach scrollRef to this div
        className="flex overflow-x-auto space-x-6 no-scrollbar" // Flexbox with horizontal scroll
      >
        {subcategories.map((subcategory) => (
          <div
            key={subcategory.subcategory_id}
            onClick={() => handleSubcategoryPress(subcategory.subcategory_id)}
            className={`cursor-pointer flex-shrink-0 w-48 sm:w-40 md:w-36 lg:w-48 flex flex-col items-center justify-center p-4 sm:p-3 md:p-2 rounded-lg transition-colors duration-300 transform hover:scale-105 ${
              selectedSubcategory === subcategory.subcategory_id
                ? "bg-blue-700 text-white border-blue-900"
                : "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-300"
            }`}
          >
            <p className="text-lg sm:text-sm md:text-base font-medium">
              {subcategory.name}
            </p>
          </div>
        ))}
      </div>

      <button
        onClick={() => handleScroll("right")}
        className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
        aria-label="Scroll right"
      >
        {/* Right arrow (could use an icon or emoji) */}
        &#9654;
      </button>
    </div>
  </div>
)}

        <Divider />
        {loading ? (
          <p>Loading products...</p>
        ) : filteredProducts.length > 0 ? (
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 mb-4 md:mb-8">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                handleAddToCart={() => handleAddToCart(product)}
                handleAddToWishlist={() => handleAddToWishlist(product)}
              />
            ))}
          </div>
        ) : (
          <p className="text-lg font-bold">No products found in this category.</p>
        )}
      </div>
      <Footer />
    </>
  );
};

export default CategoryPage;
